/*
Copyright 2026 Campsoft

SPDX-License-Identifier: AGPL-3.0-only
*/

import { type FC, useEffect } from "react";
import { type MatrixClient } from "matrix-js-sdk";
import {
  type CallMembership,
  type MatrixRTCSession,
} from "matrix-js-sdk/lib/matrixrtc";
import { Heading, Text } from "@vector-im/compound-web";
import { logger } from "matrix-js-sdk/lib/logger";

import { Config } from "../config/Config";
import { FullScreenView } from "../FullScreenView";
import {
  ElementCallError,
  ErrorCategory,
  ErrorCode,
} from "../utils/errors";
import styles from "./CampsoftGuestMode.module.css";

const AUTO_JOIN_PREFIX = "campsoft-meet-autojoin:";
const DEFAULT_INTERNAL_LOGIN_URL = "https://equipe.campsoft.com/";
const DEFAULT_END_CALL_URL = "/campsoft/call-ended.html";

export const isCampsoftGuestOnlyMode = (): boolean => {
  try {
    return Config.get().campsoft?.guest_only_mode === true;
  } catch {
    return false;
  }
};

export const isCampsoftWaitingForHostError = (error: unknown): boolean =>
  isCampsoftGuestOnlyMode() &&
  error instanceof ElementCallError &&
  error.code === ErrorCode.SFU_ERROR &&
  error.category === ErrorCategory.CONFIGURATION_ISSUE;

export const campsoftInternalLoginUrl = (): string =>
  Config.get().campsoft?.internal_login_url ?? DEFAULT_INTERNAL_LOGIN_URL;

export const campsoftEndCallUrl = (): string =>
  Config.get().campsoft?.end_call_url ?? DEFAULT_END_CALL_URL;

const campsoftGuestServerName = (): string =>
  Config.get().campsoft?.guest_server_name ??
  Config.get().default_server_config["m.homeserver"].server_name;

export const isCampsoftGuestUser = (client?: MatrixClient | null): boolean => {
  if (!isCampsoftGuestOnlyMode()) return false;
  const userId = client?.getUserId();
  return userId?.endsWith(`:${campsoftGuestServerName()}`) === true;
};

export const hasCampsoftRemoteCallMembership = (
  membershipSource:
    | Pick<MatrixRTCSession, "memberships">
    | readonly CallMembership[],
  localUserId: string | null | undefined,
): boolean => {
  const memberships =
    "memberships" in membershipSource
      ? membershipSource.memberships
      : membershipSource;

  return memberships.some(
    (membership) =>
      typeof membership.userId === "string" && membership.userId !== localUserId,
  );
};

const autoJoinKey = (roomId: string): string => `${AUTO_JOIN_PREFIX}${roomId}`;

export const enableCampsoftAutoJoinForRoom = (roomId: string): void => {
  if (!isCampsoftGuestOnlyMode()) return;
  window.sessionStorage.setItem(autoJoinKey(roomId), "1");
};

export const clearCampsoftAutoJoinForRoom = (roomId: string): void => {
  window.sessionStorage.removeItem(autoJoinKey(roomId));
};

export const shouldCampsoftAutoJoinRoom = (roomId: string): boolean =>
  isCampsoftGuestOnlyMode() &&
  window.sessionStorage.getItem(autoJoinKey(roomId)) === "1";

export const CampsoftStaticRedirect: FC<{ url?: string }> = ({
  url = campsoftEndCallUrl(),
}) => {
  useEffect(() => {
    window.location.assign(url);
  }, [url]);

  return (
    <FullScreenView>
      <div className={styles.content}>
        <div className={styles.spinner} aria-hidden />
        <Heading size="lg" weight="semibold">
          Redirecionando
        </Heading>
      </div>
    </FullScreenView>
  );
};

export const CampsoftGuestHome: FC = () => (
  <FullScreenView>
    <div className={styles.content}>
      <img
        className={styles.logo}
        src="/campsoft/campsoft-logo-horizontal-light.svg"
        alt="Campsoft"
      />
      <Heading size="lg" weight="semibold">
        Campsoft Meet
      </Heading>
      <Text className={styles.body}>
        Reuniões externas são acessadas pelo link de convite. Colaboradores
        criam e iniciam chamadas pelo Equipe com SSO corporativo.
      </Text>
      <div className={styles.actions}>
        <a className={styles.primaryLink} href={campsoftInternalLoginUrl()}>
          Abrir Equipe
        </a>
      </div>
    </div>
  </FullScreenView>
);

export const CampsoftWaitingForHost: FC = () => (
  <FullScreenView>
    <div className={styles.content}>
      <img
        className={styles.logo}
        src="/campsoft/campsoft-logo-horizontal-light.svg"
        alt="Campsoft"
      />
      <div className={styles.spinner} aria-hidden />
      <Heading size="lg" weight="semibold">
        Aguardando o anfitrião iniciar a chamada
      </Heading>
      <Text className={styles.body}>
        Você já foi autorizado a entrar. Assim que o anfitrião iniciar a
        chamada, esta tela conectará automaticamente.
      </Text>
    </div>
  </FullScreenView>
);

export const useCampsoftGuestCallEndRedirect = (
  client: MatrixClient,
  isPasswordlessUser: boolean,
  roomId: string,
): boolean => {
  const shouldRedirect = isCampsoftGuestOnlyMode() && isPasswordlessUser;

  useEffect(() => {
    if (!shouldRedirect) return;

    clearCampsoftAutoJoinForRoom(roomId);
    client
      .logout()
      .catch((error) => {
        logger.warn("Failed to logout Campsoft guest after call end", error);
      })
      .finally(() => {
        window.location.assign(campsoftEndCallUrl());
      });
  }, [client, roomId, shouldRedirect]);

  return shouldRedirect;
};
